import type { MarketplaceOrderHandoffDefaults } from "@nexus/contracts";

import { itemIdsNotWithVendor } from "./sent-scripts-map";

import type { SentScriptsMap } from "../types/marketplace.types";

export interface PartnerSendTarget {
  connectionId: string;
  vendorId: string;
  vendorName: string;
}

/** One order for one partner: only the items that partner does not have yet. */
export interface PartnerSend extends PartnerSendTarget {
  scriptIds: string[];
  briefIds: string[];
}

/** Partners that already have every item are left out: there is nothing to send them. */
export function planPartnerSends(
  map: SentScriptsMap,
  partners: readonly PartnerSendTarget[],
  scriptIds: readonly string[],
  briefIds: readonly string[],
): PartnerSend[] {
  return partners.flatMap((partner) => {
    const send = {
      ...partner,
      scriptIds: itemIdsNotWithVendor(map, scriptIds, partner.vendorId),
      briefIds: itemIdsNotWithVendor(map, briefIds, partner.vendorId),
    };
    return send.scriptIds.length + send.briefIds.length > 0 ? [send] : [];
  });
}

/**
 * Connected partners can be ticked together. Any other partner needs its own
 * connect or re-sign flow first, so picking one replaces the whole selection.
 */
export function togglePartnerSelection(
  selectedIds: readonly string[],
  vendorId: string,
  isConnected: (vendorId: string) => boolean,
): string[] {
  if (!isConnected(vendorId)) return [vendorId];
  const connectedIds = selectedIds.filter(isConnected);
  return connectedIds.includes(vendorId)
    ? connectedIds.filter((id) => id !== vendorId)
    : [...connectedIds, vendorId];
}

/**
 * Applies the user's edits from the shared handoff form onto one partner's own
 * defaults, so a field the user left alone keeps that partner's package value.
 */
export function applyHandoffEdits(
  partnerDefaults: MarketplaceOrderHandoffDefaults,
  edited: MarketplaceOrderHandoffDefaults,
  baseline: MarketplaceOrderHandoffDefaults,
): MarketplaceOrderHandoffDefaults {
  const merged = { ...partnerDefaults };
  for (const key of Object.keys(edited) as (keyof MarketplaceOrderHandoffDefaults)[]) {
    if (edited[key] !== baseline[key]) merged[key] = edited[key];
  }
  return merged;
}
